/** One click on a project's Status pages: the team's run over this site, now.
 *
 * The same run as "Run now on this site" (useRunSweepNow), with no note to write:
 * the run says which page asked. It syncs Gmail, Drive and Slack first. See
 * docs/Y2026-Q4/design-rerun-now-with-fresh-sources.md.
 */
import { useNavigate } from "react-router-dom";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import { useQueuedPass, useRunSweepNow } from "../api/hooks/useAgentPortfolio";
import { projectScheduledPath } from "../routes/learningAgentPaths";
import { formatRelative } from "../utils/formatRelative";
import { BlockingRuns } from "./BlockingRuns";
import styles from "./RerunNowButton.module.css";

export const RerunNowButton = ({
  projectId,
  slot,
  pageLabel,
}: {
  projectId: string;
  /** The agent slot the run is for: the one the page is viewing. */
  slot: string;
  /** The tab it was pressed on ("Legal"), as the run's note names it. */
  pageLabel: string;
}) => {
  const runNow = useRunSweepNow(null);
  const queuedPass = useQueuedPass(projectId);
  const navigate = useNavigate();
  const { user } = useKindeAuth();
  const started = runNow.data?.status === "started";

  const start = () => {
    if (runNow.isLoading || started) return;
    runNow.mutate(
      {
        prompt: `Started from this site's ${pageLabel} page.`,
        project_ids: [projectId],
        actor_email: user?.email ?? "",
        started_from: pageLabel,
        // A one-press rerun has nowhere to ask; the Scheduled tab's "Run now" does.
        automated_review: false,
        config: slot,
      },
      {
        onSuccess: (result) => {
          if (result.status === "started" && result.run_id) {
            navigate(projectScheduledPath(projectId, result.run_id));
          }
        },
      },
    );
  };

  // Queued, or refused because nothing is enrolled: either must be said, or the button
  // reads as broken. Short, to fit the tab row. The server's queue decides, so a request
  // queued before a reload still shows and clears once it starts; the press's own
  // answer only covers the refetch it triggers.
  const waitingOn = queuedPass.data?.queued
    ? queuedPass.data.waiting_on
    : runNow.data?.status === "queued" && queuedPass.isFetching
      ? (runNow.data.waiting_on ?? [])
      : null;
  const refused = queuedPass.data?.queued ? null : queuedPass.data?.refused;
  const refusal =
    waitingOn !== null ? (
      waitingOn.length > 0 ? (
        <>
          Queued, waiting for a free place: starts after <BlockingRuns runs={waitingOn} />.
        </>
      ) : (
        "Queued: starts within a few minutes."
      )
    ) : runNow.data && !started && runNow.data.status !== "queued" ? (
      runNow.data.detail
    ) : refused ? (
      `Not run (${formatRelative(refused.at)}): ${refused.reason}`
    ) : null;
  return (
    <span className={styles.wrap}>
      {refusal && <span className={styles.message}>{refusal}</span>}
      {runNow.error && <span className={styles.error}>{runNow.error.message}</span>}
      <button
        type="button"
        className={styles.button}
        disabled={runNow.isLoading || started}
        onClick={start}
        title="Every agent on this site, now: checks Gmail, Drive and Slack for anything new, then the whole team works the site"
      >
        {runNow.isLoading ? "Starting…" : started ? "Started" : "Rerun all agents"}
      </button>
    </span>
  );
};
